/**
 * SectionDocumentVersions — desktop-upload version history for one outline
 * section. Real proposal writing happens outside the app (Word/Google
 * Docs) — this lets the user drop in each new version and keeps a
 * download-able history.
 */
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '../ui/button';
import { Upload, Download, FileText, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return '';
  }
}

export default function SectionDocumentVersions({ pursuitId, sectionId }) {
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const fetchDocuments = useCallback(async () => {
    if (!sectionId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/outline-sections/${sectionId}/documents`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setDocuments(data.documents || []);
      }
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [pursuitId, sectionId, getAuthHeaders]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const onDrop = useCallback(
    async (files) => {
      const file = files[0];
      if (!file || !sectionId) return;
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(
          `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/outline-sections/${sectionId}/documents`,
          { method: 'POST', headers: getAuthHeaders(), body: fd }
        );
        if (!res.ok) throw new Error();
        toast.success('Version uploaded');
        fetchDocuments();
      } catch {
        toast.error('Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [pursuitId, sectionId, getAuthHeaders, fetchDocuments]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop, multiple: false });

  const download = (doc) => {
    fetch(
      `${API_URL}/api/pursuits/${pursuitId}/proposal-architect/outline-sections/${sectionId}/documents/${doc.id}/download`,
      { headers: getAuthHeaders() }
    )
      .then((res) => res.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.file_name;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download failed'));
  };

  if (!sectionId) return null;

  return (
    <div className="space-y-2">
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-500'
        }`}
        data-testid="section-doc-dropzone"
      >
        <input {...getInputProps()} />
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin mx-auto text-blue-400" />
        ) : (
          <>
            <Upload className="w-4 h-4 mx-auto mb-1 text-slate-400" />
            <p className="text-xs text-slate-400">Drop a desktop file version, or click to upload</p>
          </>
        )}
      </div>

      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin mx-auto text-slate-500" />
      ) : documents.length === 0 ? (
        <p className="text-xs text-slate-500 italic">No uploaded versions yet.</p>
      ) : (
        <div className="space-y-1">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-2 bg-slate-800/60 rounded p-2 text-xs"
              data-testid={`section-doc-row-${doc.id}`}
            >
              <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-slate-200">
                  v{doc.version} — {doc.file_name}
                  {doc.is_current && (
                    <span className="ml-1.5 text-[10px] text-emerald-400 border border-emerald-500/40 rounded px-1">
                      current
                    </span>
                  )}
                </p>
                <p className="text-slate-500">{fmtDate(doc.created_at)} · {doc.uploaded_by_name}</p>
              </div>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => download(doc)}>
                <Download className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
